import Text from 'components/Text';
import { type FieldKey, useIsCompanySite } from 'hooks';
import type {
	Column,
	FooterLinkField,
	IconLinkField,
	ImageColumn,
} from 'models/sitecore';
import type { HTMLString, TemplateString } from 'types';
import { cn } from 'utils/classNames';

import PageFooterImages from './PageFooterImages';
import PageFooterNav from './PageFooterNav';
import PageFooterSecondary from './PageFooterSecondary';

interface Props {
	/* Additional class names. */
	className?: string;

	companyName: string;
	followUsLabel: TemplateString | FieldKey;
	heading: string;

	/* Columns with small images, optionally linked. */
	imageColumns?: ImageColumn[];

	/** Information next to the logo. */
	infoText?: HTMLString;

	/* Columns of primary menu links. */
	menuColumns: Column[];

	/* Secondary links. */
	secondaryLinks: FooterLinkField[];

	/* Social media icons. */
	socialMediaLinks: IconLinkField[];
}

/** Site page footer */
export default function PageFooter({
	className,
	companyName,
	followUsLabel,
	heading,
	imageColumns,
	infoText,
	menuColumns,
	secondaryLinks,
	socialMediaLinks,
}: Props) {
	const isCompanySite = useIsCompanySite();
	return (
		<footer
			className={cn(
				'font-body text-md text-negative print:hidden forced-colors:border-t',
				isCompanySite ? 'bg-positive' : 'bg-brandDecorative',
				className,
			)}
		>
			<Text
				as="h2"
				text={heading}
				// A heading feels important enough to show it if it gets focused.
				className="sr-only-focusable focus-within:p-r-md"
			/>
			<PageFooterNav columns={menuColumns} infoText={infoText} />
			<PageFooterImages columns={imageColumns} infoText={infoText} />
			<PageFooterSecondary
				companyName={companyName}
				followUsLabel={followUsLabel}
				links={secondaryLinks}
				socialMediaLinks={socialMediaLinks}
			/>
		</footer>
	);
}
PageFooter.displayName = 'PageFooter';
